import { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Link, Skeleton, Stack, TextField, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { endpoints } from '../api/endpoints';
import { errorMessage } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { LoadingButton } from '../components/LoadingButton';
import { CopyButton } from '../components/common';

const T = {
  title: 'Sharing Control',
  signIn: 'Sign in',
  email: 'E-mail',
  password: 'Password',
  verifyTitle: 'Two-factor code',
  verifyText: 'Enter the 6-digit code from your authenticator app.',
  enrollTitle: 'Set up two-factor sign-in',
  enrollText: 'Scan the QR code with Google Authenticator, 1Password or another TOTP app, then enter the code it shows.',
  code: '6-digit code',
  verify: 'Verify',
  other: 'Use another account',
  manual: "Can't scan? Enter this key manually:",
};

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default', p: 2 }}>
      <Card sx={{ width: '100%', maxWidth: 400 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: 'primary.main', color: 'primary.contrastText', display: 'grid', placeItems: 'center' }}>
              <LockOutlinedIcon fontSize="small" />
            </Box>
            <Typography variant="h6">{T.title}</Typography>
          </Stack>
          {children}
        </CardContent>
      </Card>
    </Box>
  );
}

function CodeForm({ onDone }: { onDone: () => void }) {
  const { setSignedIn } = useAuth();
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await endpoints.totpVerify(code);
      setSignedIn(r.user, 'passed');
      onDone();
    } catch (e2) {
      setErr(errorMessage(e2));
      setCode('');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit}>
      <Stack spacing={2}>
        {err && <Alert severity="error">{err}</Alert>}
        <TextField
          autoFocus
          label={T.code}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', style: { letterSpacing: '0.35em', fontSize: '1.4rem', textAlign: 'center' } }}
          size="medium"
        />
        <LoadingButton type="submit" variant="contained" size="large" loading={busy} disabled={code.length !== 6}>
          {T.verify}
        </LoadingButton>
      </Stack>
    </form>
  );
}

function Enroll() {
  const [data, setData] = useState<{ otpauthUrl: string; qrSvg: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    endpoints.totpEnroll().then(setData, (e) => setErr(errorMessage(e)));
  }, []);
  const secret = data ? new URL(data.otpauthUrl.replace('otpauth://', 'https://')).searchParams.get('secret') : null;
  return (
    <Stack spacing={2}>
      <Typography variant="h6">{T.enrollTitle}</Typography>
      <Typography variant="body2" color="text.secondary">
        {T.enrollText}
      </Typography>
      {err && <Alert severity="error">{err}</Alert>}
      <Box sx={{ alignSelf: 'center', width: 200, height: 200, bgcolor: '#fff', borderRadius: 2, p: 1, '& svg': { width: '100%', height: '100%' } }}>
        {data ? <Box aria-label="TOTP QR code" role="img" sx={{ width: '100%', height: '100%' }} dangerouslySetInnerHTML={{ __html: data.qrSvg }} /> : <Skeleton variant="rectangular" width="100%" height="100%" />}
      </Box>
      {secret && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {T.manual}
          </Typography>
          <Stack direction="row" alignItems="center">
            <Typography sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>{secret}</Typography>
            <CopyButton text={secret} />
          </Stack>
        </Box>
      )}
      <CodeForm onDone={() => {}} />
    </Stack>
  );
}

export function LoginPage() {
  const { status, setSignedIn, logout } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'mfa' || status === 'enroll') {
    return (
      <Shell>
        {status === 'enroll' ? (
          <Enroll />
        ) : (
          <Stack spacing={2}>
            <Typography variant="h6">{T.verifyTitle}</Typography>
            <Typography variant="body2" color="text.secondary">
              {T.verifyText}
            </Typography>
            <CodeForm onDone={() => {}} />
          </Stack>
        )}
        <Link component="button" variant="body2" onClick={() => void logout()} sx={{ mt: 2 }}>
          {T.other}
        </Link>
      </Shell>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await endpoints.login(email.trim(), password);
      setSignedIn(r.user, r.mfa);
    } catch (e2) {
      setErr(errorMessage(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <form onSubmit={submit}>
        <Stack spacing={2}>
          {status === 'offline' && <Alert severity="warning">The panel server does not answer. Check the connection and try again.</Alert>}
          {err && <Alert severity="error">{err}</Alert>}
          <TextField label={T.email} type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus size="medium" required />
          <TextField label={T.password} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} size="medium" required />
          <LoadingButton type="submit" variant="contained" size="large" loading={busy}>
            {T.signIn}
          </LoadingButton>
          {import.meta.env.VITE_MOCK === '1' && (
            <Typography variant="caption" color="text.secondary">
              Mock mode: any e-mail and password, then any 6-digit code (not 000000). An e-mail starting with “new” shows the enrolment screen.
            </Typography>
          )}
        </Stack>
      </form>
    </Shell>
  );
}

export function SplashScreen() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
      <Button disabled>Loading…</Button>
    </Box>
  );
}
